import { doc, onSnapshot } from 'firebase/firestore'
import localforage from 'localforage'
import moment, { Moment } from 'moment'
import React, { useMemo } from 'react'
import { createSearchParams } from 'react-router-dom'
import { compareGraphParams, getFilterKeys, getNewLastDate } from 'src/components/NewFilterComponent/utils/filterUtils'
import { firebaseDb } from 'src/services/firebase-compat'
import { DateFilterConfigResponse, FiltersForQuery } from 'src/services/openApiV2'
import { sendSlackNotification } from 'src/utils/api'
import { AccessLevelEnum, automaticallyUpdateFiltersDuration, filterNames, swrRefreshIntervalMilliseconds } from 'src/utils/config/config'
import { get } from 'src/utils/config/lodashUtils'
import { isInternalUser } from 'src/utils/functions'
import { getAccessLevel } from 'src/utils/functions/accessLevel'
import { setLocal } from 'src/utils/functions/localStorage'
import { getModuleFromPathname } from 'src/utils/functions/moduleUtils'
import { pluralize } from 'src/utils/functions/pluralize'
import useSWR from 'swr'
import { useAuth } from '../AuthContext'
import { useDrillDownContext } from '../DrillDownContext'
import { useErrorData } from '../ErrorContext'
import { usePrivateLayoutDrawerContext } from '../PrivateLayoutDrawerContext'
import { useSnackData } from '../SnackContext'
import { useSubChainContext } from '../SubChainContext'
import useFilterDataServiceHook from './hooks/filterDataServiceHook'
import { useFilterApiConfig } from './hooks/useFilterApiConfig'
import { DateRangeType, FilterGraph } from './utils/FilterGraph'
import filterGraphManager from './utils/FilterGraphManager'
import { getDataFromIndexedDB, saveDataToIndexedDB } from './utils/NewFilterIndexDB'
import { FilterPerformanceMetrics, FilterPerformanceMetricsV1, HydrationState } from './utils/performanceTypes'
import { posthogPerformanceReporterV1 } from 'src/utils/metrics/posthogReporter'
import { ApiFilterResponseType, BusinessRefreshBnameLevelData, UserBNameNoAuthType } from './utils/types'
import { updateObserverVisibleNodesFromGraph } from './utils/updateObserverFunctions'
import { checkedIfAllOptionsSelected, getFilterDataForUser, getFilterDataSlugs, hasFilterDataChanged } from './utils/validateFilterData'

export interface getFiltersV2ReturnType extends FiltersForQuery {}

type GetFilterValues = <T extends 'array' | 'object'>(
  filterKeys: string[],
  obj?: { dataType?: T; dateWithHyphen?: boolean; emptyForAllSelected?: boolean }
) => T extends 'array' ? string[] : getFiltersV2ReturnType

export interface NewFilterContextInterface {
  businessRefreshBnameLevelData: BusinessRefreshBnameLevelData[]
  userAssignedBnamesJoined: string
  userAssignedBnames: UserBNameNoAuthType['items']
  activeGraph: FilterGraph | null
  activeGraphId: string
  mainGraph: FilterGraph
  setActiveGraphId: (graphId: string) => void
  updateActiveGraph: (graphId: string, singleSelectFilterKeys?: string[], allowedFilters?: { [key: string]: (string | number)[] }) => void
  filterConfig: {
    bNameEnabled: boolean
    vbNameEnabled: boolean
    vbPlatformEnabled: boolean
    amNameEnabled: boolean
    unionSearchSelects: boolean
    showNewFilters: boolean
    franchiseeNameEnabled: boolean
  }
  reRenderVar: number
  reRenderFilterContext: () => void
  dateRange: DateRangeType
  lastDate: Moment
  getFilterValues: GetFilterValues
  getFilters: (filterKeys: string[]) => string[]
  getFiltersV2: (filterList?: string[], dateWithHyphen?: boolean, emptyForAllSelected?: boolean) => getFiltersV2ReturnType
  getAllPossibleFilters: (filterKeys: string[], graphId?: string) => { [key: string]: (string | number)[] }
  getUrlWithFilters: () => string
  selectFiltersFromContext: (filterKey: string, values: (string | number)[]) => void
  refreshFilters: (uid?: string, org?: string) => Promise<void>
  filterDataLoading: boolean
  isFilterChanged: boolean
  showFilterChangedNotification: boolean
  setShowFilterChangedNotification: React.Dispatch<React.SetStateAction<boolean>>
  updateFiltersWithNewData: (isManualUpdate?: boolean) => void
  autoUpdateFiltersProgress: number
  moduleDateFilterData: DateFilterConfigResponse | null
  moduleDateFilterLoading: boolean
  moduleLastDates: { [key: string]: Moment }
  activeModuleEndDate: Moment | null
  userAssignedBnamesSwr: UserBNameNoAuthType['items']
  allFiltersDataForUser: ApiFilterResponseType[]
  // Performance tracking
  performanceMetrics: {
    t1_ms: number | null // Cache load time
    t2_ms: number | null // API completion time
    loadTime_ms: number | null // User-perceived time
    cacheHit: boolean
    dataWasStale: boolean
    hydrationState: HydrationState
  }
  cacheStats: any | null
}

export const NewFilterContext = React.createContext({} as NewFilterContextInterface)

export const useNewFilterContext = () => React.useContext(NewFilterContext)

interface NewFilterContextProviderProps {
  children: React.ReactNode
}

export default function NewFilterContextProvider(props: NewFilterContextProviderProps) {
  const { orgConfig, currentUser, setCurrentUser } = useAuth()
  const email = get(currentUser, 'email', '')
  const org = get(currentUser, 'org', '')

  const { currentUserAccessibleSubChainList, initialLoading: subChainInitialLoading } = useSubChainContext()
  const { getAllFiltersData, getUserAssignedBNamesData, getChainFiltersV1, getDateFilterConfig } = useFilterDataServiceHook()
  const {
    getDrillDownFilter: getDrillDownContextFilter,
    setDateRange: setDrillDownDateRange,
    stack: drillDownStack,
    setAllFiltersData: setDrillDownAllFiltersData,
    setBusinessRefreshBnameData: setDrillDownBusinessRefreshBnameData
  } = useDrillDownContext()
  const { categorizedRoutes } = usePrivateLayoutDrawerContext()
  const { openSuccess } = useSnackData()
  const { handleError } = useErrorData()
  const { internal_enabled, external_enabled, loading: filterApiConfigLoading } = useFilterApiConfig()

  // ************************** state variables ***************************
  const [tempFilterDataLoading, setFilterDataLoading] = React.useState<boolean>(true)
  const [isFilterChanged, setIsFilterChanged] = React.useState<boolean>(false)
  const [showFilterChangedNotification, setShowFilterChangedNotification] = React.useState<boolean>(false)
  const [autoUpdateFiltersProgress, setAutoUpdateFiltersProgress] = React.useState<number>(0)
  const [reRenderVar, setReRenderVar] = React.useState(0)
  const [businessRefreshBnameLevelData, setBusinessRefreshBnameLevelData] = React.useState<BusinessRefreshBnameLevelData[]>([])

  const [triggerSwr, setTriggerSwr] = React.useState(0)
  const [moduleDateFilterLoading, setModuleDateFilterLoading] = React.useState<boolean>(true)
  const [moduleDateFilterData, setModuleDateFilterData] = React.useState<DateFilterConfigResponse | null>(null)

  // ************************** PERFORMANCE TRACKING ***************************
  const [hydrationState, setHydrationState] = React.useState<HydrationState>(HydrationState.INIT)
  const [cacheStats, setCacheStats] = React.useState<any | null>(null)

  // Performance timing refs (general)
  const t1StartRef = React.useRef<number | null>(null)
  const t1EndRef = React.useRef<number | null>(null)
  const t2StartRef = React.useRef<number | null>(null)
  const t2EndRef = React.useRef<number | null>(null)
  const cacheDataRef = React.useRef<any>(null)
  const hasReportedMetricsRef = React.useRef(false)

  // API-specific T2 timing refs (actual API fetch time)
  // Note: T1 (cache) is general/shared - tracked by t1StartRef/t1EndRef
  const newApiT2StartRef = React.useRef<number | null>(null)
  const newApiT2EndRef = React.useRef<number | null>(null)
  // Old API uses two parallel calls, so we track start time and end time of each
  const oldApiT2StartRef = React.useRef<number | null>(null)
  const oldApiGetAllFiltersEndRef = React.useRef<number | null>(null)
  const oldApiGetUserBnamesEndRef = React.useRef<number | null>(null)
  // Track if data is stale due to org/access level change (not just Firestore updates)
  const wasRefreshTriggeredRef = React.useRef<boolean>(false)

  const sessionMetricsRef = React.useRef({
    totalLoads: 0,
    totalT1: 0,
    totalT2: 0,
    totalLoadTime: 0,
    cacheHits: 0,
    staleCount: 0
  })

  // Helper function to reset performance tracking for new load cycles
  const resetPerformanceTracking = (isRefreshTriggered: boolean = false) => {
    console.log('🔄 [FilterContext] Resetting performance tracking for new load cycle', { isRefreshTriggered })
    // Reset general timing (T1 for cache, T2 for overall)
    t1StartRef.current = null
    t1EndRef.current = null
    t2StartRef.current = null
    t2EndRef.current = null

    // Reset API-specific T2 timing (actual API fetch time)
    newApiT2StartRef.current = null
    newApiT2EndRef.current = null
    oldApiT2StartRef.current = null
    oldApiGetAllFiltersEndRef.current = null
    oldApiGetUserBnamesEndRef.current = null

    cacheDataRef.current = null
    hasReportedMetricsRef.current = false
    // Track if this reset was due to org/access level change
    wasRefreshTriggeredRef.current = isRefreshTriggered
    setHydrationState(HydrationState.INIT)
  }

  // ************************ SWR hooks *************************
  const fallbackEmptyArrayRef = React.useRef([])

  // Check if user is internal
  const isInternal = React.useMemo(() => isInternalUser(currentUser), [currentUser])

  // Determine which API to use based on Firebase config
  const shouldUseNewApi = React.useMemo(() => {
    if (filterApiConfigLoading) return null // Wait for config to load
    const result = isInternal ? internal_enabled : external_enabled

    return result
  }, [isInternal, internal_enabled, external_enabled, filterApiConfigLoading])

  // Old APIs - for users where shouldUseNewApi is false
  const { data: tempAllFiltersDataSwr, isLoading: allFiltersDataLoadingSwr } = useSWR(
    shouldUseNewApi === false ? ['filtersData', get(currentUser, 'org', ''), triggerSwr] : null,
    async ([, org]: [string, string, number]) => {
      // Start T2 timing for old API (both calls start at roughly the same time)
      if (!oldApiT2StartRef.current) {
        oldApiT2StartRef.current = performance.now()
        console.log('⏱️ [FilterContext] Old API T2 started (getAllFiltersData)')
      }
      const result = await getAllFiltersData(org)
      // Track when this specific call ends
      oldApiGetAllFiltersEndRef.current = performance.now()
      console.log('⏱️ [FilterContext] Old API getAllFiltersData completed:', oldApiGetAllFiltersEndRef.current - (oldApiT2StartRef.current || 0), 'ms')
      return result
    },
    {
      refreshInterval: swrRefreshIntervalMilliseconds,
      revalidateOnFocus: false,
      fallbackData: fallbackEmptyArrayRef.current,
      errorRetryCount: 1
    }
  )

  const { data: userAssignedBnamesSwr_old, isLoading: userAssignedBnamesLoadingSwr } = useSWR(
    shouldUseNewApi === false ? ['userAssignedBnames', get(currentUser, 'uid', ''), triggerSwr] : null,
    async () => {
      // Start T2 timing for old API (both calls start at roughly the same time)
      if (!oldApiT2StartRef.current) {
        oldApiT2StartRef.current = performance.now()
        console.log('⏱️ [FilterContext] Old API T2 started (getUserAssignedBNamesData)')
      }
      const result = await getUserAssignedBNamesData()
      // Track when this specific call ends
      oldApiGetUserBnamesEndRef.current = performance.now()
      console.log('⏱️ [FilterContext] Old API getUserAssignedBNamesData completed:', oldApiGetUserBnamesEndRef.current - (oldApiT2StartRef.current || 0), 'ms')
      return result
    },
    {
      refreshInterval: swrRefreshIntervalMilliseconds,
      revalidateOnFocus: false,
      fallbackData: fallbackEmptyArrayRef.current,
      errorRetryCount: 1
    }
  )

  // New API - for users where shouldUseNewApi is true
  const { data: chainFiltersV1Response, isLoading: chainFiltersV1Loading } = useSWR(
    shouldUseNewApi === true ? ['chainFiltersV1', get(currentUser, 'uid', ''), triggerSwr] : null,
    async () => {
      // Start T2 timing for new API
      if (!newApiT2StartRef.current) {
        newApiT2StartRef.current = performance.now()
      }
      const result = await getChainFiltersV1()
      // End T2 timing for new API
      if (!newApiT2EndRef.current) {
        newApiT2EndRef.current = performance.now()
      }
      return result
    },
    {
      refreshInterval: swrRefreshIntervalMilliseconds,
      revalidateOnFocus: false,
      errorRetryCount: 1
    }
  )

  // Extract the data array from the response
  const chainFiltersV1Data = React.useMemo(() => {
    if (Array.isArray(chainFiltersV1Response)) {
      return chainFiltersV1Response
    }
    if (chainFiltersV1Response && typeof chainFiltersV1Response === 'object') {
      return chainFiltersV1Response.data
    }
  }, [chainFiltersV1Response])

  // Helper function to see if we have the new API data
  const hasNewApiData = React.useMemo((): boolean => {
    return shouldUseNewApi === true && chainFiltersV1Data != null && Array.isArray(chainFiltersV1Data)
  }, [shouldUseNewApi, chainFiltersV1Data])

  // Transform new API data to populate userAssignedBnamesSwr
  const userAssignedBnamesSwr: UserBNameNoAuthType['items'] = React.useMemo((): UserBNameNoAuthType['items'] => {
    // For new API users, extract unique b_name and b_name_id from chainFiltersV1Data
    if (hasNewApiData ){
      // Create a Map to store unique b_name_id entries
      const uniqueBnamesMap = new Map<number, UserBNameNoAuthType['items'][0]>()

      chainFiltersV1Data.forEach((item) => {
        if (item.b_name_id && item.b_name) {
          // Only add if we haven't seen this b_name_id before
          if (!uniqueBnamesMap.has(item.b_name_id)) {
            uniqueBnamesMap.set(item.b_name_id, {
              id: item.b_name_id,
              name: item.b_name,
              // Adding placeholder values for compatibility with UserBNameNoAuthType
              address: '',
              owner_id: null,
              owner_name: null
            })
          }
        }
      })

      const result = Array.from(uniqueBnamesMap.values())

      return result
    }

    // For old API users, return the original data
    return (userAssignedBnamesSwr_old || []) as UserBNameNoAuthType['items']
  }, [hasNewApiData, chainFiltersV1Data, userAssignedBnamesSwr_old])

  const filterDataLoading = React.useMemo(() => {
    // When using new API, we don't need to wait for SubChainContext
    // but we DO need to wait for chainFiltersV1Data to be available
    // so that franchise filter visibility is calculated correctly
    if (shouldUseNewApi === true) {
      // Wait for both: graph loaded (tempFilterDataLoading) AND chainFiltersV1Data available
      return tempFilterDataLoading || chainFiltersV1Loading
    }
    // For old API, wait for both filter data and subchain data
    if (shouldUseNewApi === false) {
      return tempFilterDataLoading || subChainInitialLoading
    }
    // If shouldUseNewApi is null (config still loading), show loading state
    return true
  }, [tempFilterDataLoading, subChainInitialLoading, shouldUseNewApi, chainFiltersV1Data])

  const allFiltersDataSwr = React.useMemo(() => {
    // For users using new API, use the new API data directly
    if (hasNewApiData) {
      // The new API returns data already formatted as ApiFilterResponseType[]
      // Add franchisee mapping if needed
      const unmappedFranchiseLocations = Array.from(
        new Set(
          chainFiltersV1Data
            .filter((obj) => !obj?.franchisee_name || obj.franchisee_name.trim() === '')
            .map((obj) => obj?.b_name)
            .filter(Boolean) // Remove null/undefined/empty values
        )
      )

      return chainFiltersV1Data.map((obj) => {
        const franchisee_id = typeof obj.franchisee_id === 'number' ? obj.franchisee_id : -1
        const franchisee_name =
          (obj.franchisee_name && obj.franchisee_name.trim() !== '' ? obj.franchisee_name : null) ||
          (unmappedFranchiseLocations?.length
            ? `Unassigned ${pluralize(filterNames.b_name, unmappedFranchiseLocations?.length)} (${unmappedFranchiseLocations?.length})`
            : `Unassigned ${filterNames.b_name}`)
        return {
          ...obj,
          franchisee_name,
          franchisee_id
        }
      })
    }

    // For users using old API, use the old API data
    if (!Array.isArray(tempAllFiltersDataSwr)) {
      return []
    }

    const unmappedFranchiseLocations = Array.from(
      new Set(
        tempAllFiltersDataSwr
          .filter((obj) => !obj?.franchisee_name || obj.franchisee_name.trim() === '')
          .map((obj) => obj?.b_name)
          .filter(Boolean) // Remove null/undefined/empty values
      )
    )

    const result = tempAllFiltersDataSwr.map((obj) => {
      const franchisee_id = typeof obj.franchisee_id === 'number' ? obj.franchisee_id : -1
      const franchisee_name =
        (obj.franchisee_name && obj.franchisee_name.trim() !== '' ? obj.franchisee_name : null) ||
        (unmappedFranchiseLocations?.length
          ? `Unassigned ${pluralize(filterNames.b_name, unmappedFranchiseLocations?.length)} (${unmappedFranchiseLocations?.length})`
          : 'Unassigned Locations')
      return {
        ...obj,
        franchisee_name,
        franchisee_id
      }
    })

    return result
  }, [tempAllFiltersDataSwr, hasNewApiData])

  const allFiltersDataForUser = React.useMemo(() => {
    // For users using new API, the data is already filtered for the user
    if (hasNewApiData) {
      return allFiltersDataSwr
    }

    // For users using old API, use the existing filtering logic
    return getFilterDataForUser(
      allFiltersDataSwr as any,
      userAssignedBnamesSwr,
      [AccessLevelEnum.BUSINESS_ADMIN, AccessLevelEnum.FINANCE_TEAM].includes(getAccessLevel(currentUser))
    )
  }, [allFiltersDataSwr, userAssignedBnamesSwr, hasNewApiData])

  const userAssignedBnamesJoined = React.useMemo(() => {
    // For users using new API, extract unique b_names from the new API data
    if (hasNewApiData) {
      const uniqueBNames = [...new Set(chainFiltersV1Data.map((item) => item.b_name))]
      return uniqueBNames.join('|')
    }

    // For users using old API, use the existing logic
    const accessLevel = getAccessLevel(currentUser)
    if (accessLevel === AccessLevelEnum.BUSINESS_ADMIN || accessLevel === AccessLevelEnum.FINANCE_TEAM) {
      return ''
    }
    return userAssignedBnamesSwr?.map((obj) => obj.name).join('|')
  }, [userAssignedBnamesSwr, hasNewApiData, chainFiltersV1Data])

  React.useEffect(() => {
    // Wait for Firebase config to load
    if (filterApiConfigLoading) {
      return
    }

    // For users using new API, check the new API loading state
    if (shouldUseNewApi === true) {
      if (chainFiltersV1Loading || !chainFiltersV1Data || !Array.isArray(chainFiltersV1Data)) {
        return
      }
    }
    else{
      // For users using old API, check the old API loading states
      if (userAssignedBnamesLoadingSwr || allFiltersDataLoadingSwr || !allFiltersDataSwr || !userAssignedBnamesSwr) {
        return
      }
    }

    if (currentUser) {
      checkFilterDataChanged()
    }
  }, [
    userAssignedBnamesLoadingSwr,
    allFiltersDataLoadingSwr,
    allFiltersDataSwr,
    userAssignedBnamesSwr,
    currentUser,
    shouldUseNewApi,
    chainFiltersV1Loading,
    chainFiltersV1Data,
    chainFiltersV1Response,
    filterApiConfigLoading
  ])

  // Log API usage for monitoring

  // ? Active graph key from FilterGraphManager
  const activeGraphIdRef = React.useRef('main')
  const activeGraphId = activeGraphIdRef.current
  const activeGraph = { current: filterGraphManager.getGraph(activeGraphId) }
  const setActiveGraphId = (graphId: string) => {
    activeGraphIdRef.current = graphId
    activeGraph.current = filterGraphManager.getGraph(graphId)
  }
  const mainGraph = filterGraphManager.getGraph('main')

  const filterConfig: NewFilterContextInterface['filterConfig'] = React.useMemo(() => {
    const bNameEnabled: boolean = get(orgConfig, 'filterConfig.b_name', false)
    const vbNameEnabled: boolean = get(orgConfig, 'filterConfig.vb_name', false)
    const vbPlatformEnabled: boolean = get(orgConfig, 'filterConfig.vb_platform', false)
    const amNameEnabled: boolean = get(orgConfig, 'filterConfig.am_name', false)

    // Franchise filter logic
    let franchiseeNameEnabled: boolean = false

    // If org config explicitly disables franchise filter, respect that
    if (get(orgConfig, 'filterConfig.franchisee_name', null) === false) {
      franchiseeNameEnabled = false
    } else if (shouldUseNewApi === true) {
      // For new API users, check the number of unique franchises in the data
      // This runs synchronously with the same data that populates other filters
      if (chainFiltersV1Data && Array.isArray(chainFiltersV1Data)) {
        const uniqueFranchises = new Set(
          chainFiltersV1Data
            .map((item: ApiFilterResponseType) => item.franchisee_id)
            .filter((id: number | null | undefined) => id !== null && id !== undefined)
        )
        // Show franchise filter if there are more than 1 unique franchise
        franchiseeNameEnabled = uniqueFranchises.size > 1
      }
    } else if (shouldUseNewApi === false) {
      // For old API users, use the existing logic (Store Managers or users with subchain access)
      const isStoreManager = getAccessLevel(currentUser) === AccessLevelEnum.STORE_MANAGER
      franchiseeNameEnabled = isStoreManager || currentUserAccessibleSubChainList.length > 0
    }

    const unionSearchSelects: boolean = false && !get(orgConfig, 'filterConfig.search_select', false)
    const showNewFilters: boolean = true

    return {
      bNameEnabled,
      vbNameEnabled,
      vbPlatformEnabled,
      amNameEnabled,
      franchiseeNameEnabled,
      unionSearchSelects,
      showNewFilters
    }
  }, [orgConfig, currentUserAccessibleSubChainList, currentUser, shouldUseNewApi, chainFiltersV1Data])

  /**
   * this function will refresh the FilterContext because
   * change in FilterGraph instance does not re-renders the context
   */
  const reRenderFilterContext = () => {
    if (activeGraphId === 'main') {
      const defaultFilterKeys = getFilterKeys()
      const selectedFilterValuesJoined = {}
      const result = getFiltersV2(defaultFilterKeys)
      defaultFilterKeys.forEach((key) => {
        selectedFilterValuesJoined[key] = result[`${key}_in`]
      })
      saveSelectFiltersToSessionStorage(JSON.stringify(selectedFilterValuesJoined))
      // saveDataToIndexedDB('SelectedFilters', selectedFilterValuesJoined)
    }
    setReRenderVar((prev) => prev + 1)
  }

  // *************************** graph actions ****************************
  /**
   * this function will add the FilterGraph instance to filterGraphManager
   * @param graphId
   * @param param
   */
  const setGraphToManager = (
    graphId: string,
    data: ApiFilterResponseType[],
    singleSelectFilterKeys?: string[],
    allowedFilters?: { [key: string]: (string | number)[] }
  ) => {
    const filterData = [...data]
    filterGraphManager.setGraph(graphId, filterData, businessRefreshBnameLevelData, singleSelectFilterKeys, allowedFilters)
  }

  const updateActiveGraph = (graphId: string, singleSelectFilterKeys?: string[], allowedFilters?: { [key: string]: (string | number)[] }) => {
    // ? check if graph with id already exists, and special params have not changed
    if (
      filterGraphManager.getGraphIds().includes(graphId) &&
      compareGraphParams(filterGraphManager.getParams(graphId), singleSelectFilterKeys, allowedFilters)
    ) {
      setActiveGraphId(graphId)
      return
    }
    let filtersData = allFiltersDataForUser
    if (allowedFilters) {
      filtersData = filtersData.filter((obj) => {
        return Object.keys(allowedFilters).every((allowedKey) => allowedFilters[allowedKey].includes(obj[allowedKey]))
      })
    }
    setGraphToManager(graphId, filtersData, singleSelectFilterKeys, allowedFilters)
    setActiveGraphId(graphId)
    reRenderFilterContext()
  }

  const createGraphInstances = (propAllFilterDataForUser?: ApiFilterResponseType[]) => {
    const allFiltersDataUserLevel = propAllFilterDataForUser ? propAllFilterDataForUser : allFiltersDataForUser

    const allGraphIds = filterGraphManager.getGraphIds()

    if (allGraphIds.length === 0) {
      setGraphToManager('main', allFiltersDataUserLevel)
    } else {
      // ? update each instance with new data
      allGraphIds.forEach((graphId) => {
        // ? save special params
        const specialParams = filterGraphManager.getParams(graphId)
        // ? save selected state
        const selectedValues = {}
        const graph = filterGraphManager.getGraph(graphId)
        Object.keys(graph.getFilters()).forEach((key) => {
          if (graph.getSelectedNodesByKey(key).size === graph.getAllPossibleNodesByKey(key).length) return
          graph.getSelectedNodesByKey(key).forEach((node) => {
            if (key in selectedValues) {
              selectedValues[key].push(node.value)
            } else {
              selectedValues[key] = [node.value]
            }
          })
        })
        // ? create new graph with params
        let filtersData = allFiltersDataUserLevel
        if (specialParams?.allowedFilters) {
          filtersData = filtersData?.filter((obj) => {
            return Object.keys(specialParams.allowedFilters)?.every((allowedKey) => specialParams.allowedFilters[allowedKey]?.includes(obj[allowedKey]))
          })
        }
        setGraphToManager(graphId, filtersData, specialParams.singleSelectFilterKeys, specialParams.allowedFilters)
        // ? restore selected state
        Object.keys(selectedValues).forEach((key) => {
          if (key !== 'b_name_id') selectFiltersFromContext(key, selectedValues[key], graphId)
        })
      })
    }
    filterGraphManager.setFilterDataSlugs(getFilterDataSlugs(allFiltersDataForUser))

    reRenderFilterContext()
    setFilterDataLoading(false)

    // Track T2 completion here - this is when filter data is actually ready
    if (!t2EndRef.current && t2StartRef.current) {
      t2EndRef.current = performance.now()
      setHydrationState(HydrationState.COMPLETE)
      reportPerformanceMetrics()

      // Reset refs for next measurement cycle after a delay
      setTimeout(() => {
        t1StartRef.current = null
        t1EndRef.current = null
        t2StartRef.current = null
        t2EndRef.current = null
        cacheDataRef.current = null
        hasReportedMetricsRef.current = false
        setHydrationState(HydrationState.INIT)
      }, 5000)
    }

    saveDataToIndexedDB('AllFiltersData', allFiltersDataUserLevel)
  }

  // ********************* business data fetching ******************************
  // Not needed anymore as we are having different date ranges for each module
  // const getBusinessRefreshBnameLevelData = (businessData: BusinessRefreshData[]) => {
  //   try {
  //     const data = businessData.map((item) => {
  //       return {
  //         ...item,
  //         success_rate: item.success_rate || 0,
  //         lastDate: item.last_date
  //       }
  //     })
  //     if (data && Array.isArray(data) && data.length > 0) {
  //       setBusinessRefreshBnameLevelData(data)
  //       const graphIds = filterGraphManager.getGraphIds()
  //       graphIds.forEach((graphId) => {
  //         const graph = filterGraphManager.getGraph(graphId)
  //         const newLastDate = getNewLastDate(data, graph)
  //         graph.setLastDate(newLastDate)
  //         updateDateRangeFromLastDate(graph, newLastDate, true)
  //       })
  //     }
  //   } catch (err) {
  //     handleError(err.message)
  //   }
  // }

  const fetchFiltersDataFromLocalAndAPI = (org?: string) => {
    // Start T2 timing at component mount
    if (!t2StartRef.current) {
      t2StartRef.current = performance.now()
      setHydrationState(HydrationState.INIT)
    }
    getFiltersDataFromIndexedDb()
  }

  // ? first load
  React.useEffect(() => {
    fetchFiltersDataFromLocalAndAPI()
  }, [])

  // Send session metrics on unmount
  React.useEffect(() => {
    return () => {
      const sessionMetrics = sessionMetricsRef.current
      if (sessionMetrics.totalLoads > 0) {
        const avgSessionMetrics = {
          totalLoads: sessionMetrics.totalLoads,
          avgT1: sessionMetrics.totalT1 / sessionMetrics.totalLoads,
          avgT2: sessionMetrics.totalT2 / sessionMetrics.totalLoads,
          avgLoadTime: sessionMetrics.totalLoadTime / sessionMetrics.totalLoads,
          cacheHitRate: sessionMetrics.cacheHits / sessionMetrics.totalLoads,
          staleRate: sessionMetrics.staleCount / sessionMetrics.totalLoads,
          errorRate: 0
        }
        // Session metrics removed - focus on filter performance only for now
      }
    }
  }, [])

  // ? after org change
  React.useEffect(() => {
    let unsubscribe
    if (currentUser && currentUser.org) {
      const query = doc(firebaseDb, `users/${currentUser.uid}`)
      unsubscribe = onSnapshot(query, (snapshot) => {
        const data = snapshot.data()

        if (!data) return

        const currentUserAccessLevel = getAccessLevel(currentUser)
        const dataAccessLevel = getAccessLevel(data)
        if (data?.org !== currentUser?.org || dataAccessLevel !== currentUserAccessLevel) {
          setCurrentUser({
            ...currentUser,
            ...data
          })
          setLocal('userOrg', data.org)
          refreshFilters()
        }
      })
    }

    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [currentUser])

  // ********************** swr data update handlers *************************
  const checkFilterDataChanged = () => {
    // Ensure graph instances are created if needed
    // T2 timing is tracked inside createGraphInstances() when loading actually completes
    if (filterGraphManager.getFilterDataSlugs().size === 0) {
      createGraphInstances()
    }

    if (hasFilterDataChanged(allFiltersDataForUser, filterGraphManager.getFilterDataSlugs())) {
      setIsFilterChanged(true)
      setShowFilterChangedNotification(true)

      // Track staleness in session metrics
      if (sessionMetricsRef.current) {
        sessionMetricsRef.current.staleCount++
      }

      sendSlackNotification({
        message: 'Filters data change detected',
        channel: 'fe-logs',
        title:
          `Filters data changed for\n` +
          (org ? `\`org: ${org}\`\n` : ``) +
          (email ? `\`user: ${email}\`\n` : ``) +
          `\`page URL: <${window.location.href}|${window.location.pathname}>\`\n`
      })
      setTimeout(() => {
        updateFiltersWithNewData()
      }, automaticallyUpdateFiltersDuration)
    }
  }

  // Report performance metrics to PostHog
  const reportPerformanceMetrics = () => {
    if (hasReportedMetricsRef.current) return
    hasReportedMetricsRef.current = true

    // General timing metrics
    const t1_ms = t1EndRef.current && t1StartRef.current ? t1EndRef.current - t1StartRef.current : null
    const t2_ms = t2EndRef.current && t2StartRef.current ? t2EndRef.current - t2StartRef.current : null

    // API-specific T2 timing metrics (actual API fetch time)
    // Note: T1 (cache) is general/shared - use t1_ms above
    const new_api_t2_ms = newApiT2EndRef.current && newApiT2StartRef.current
      ? newApiT2EndRef.current - newApiT2StartRef.current
      : null
    // Old API T2 = time from start until BOTH parallel calls complete (max of end times)
    const oldApiLastEndTime = Math.max(
      oldApiGetAllFiltersEndRef.current || 0,
      oldApiGetUserBnamesEndRef.current || 0
    )
    const old_api_t2_ms = oldApiT2StartRef.current && oldApiLastEndTime > 0
      ? oldApiLastEndTime - oldApiT2StartRef.current
      : null

    const usedCache = !!cacheDataRef.current
    // Data is stale if: Firestore detected changes OR org/access level was changed
    const dataWasStale = isFilterChanged || wasRefreshTriggeredRef.current

    // User-perceived load time
    const loadTime_ms = dataWasStale || !usedCache ? t2_ms || 0 : t1_ms || t2_ms || 0

    // Update session metrics
    sessionMetricsRef.current.totalLoads++
    if (t1_ms) sessionMetricsRef.current.totalT1 += t1_ms
    if (t2_ms) sessionMetricsRef.current.totalT2 += t2_ms
    sessionMetricsRef.current.totalLoadTime += loadTime_ms
    if (usedCache) sessionMetricsRef.current.cacheHits++

    // Base metrics
    const metrics: FilterPerformanceMetrics = {
      t1_ms: t1_ms || 0,
      t2_ms: t2_ms || 0,
      loadTime_ms,
      cache_hit: usedCache && !dataWasStale,
      data_was_stale: dataWasStale,
      total_filters_count: allFiltersDataForUser?.length || 0,
      session_id: `filter-ctx-${Date.now().toString(36)}`,
      timestamp: Date.now(),
      user_id: currentUser?.uid || null,
      page_path: window.location.pathname
    }

    // V1 metrics with API-specific T2 data
    // Note: T1 (cache) is general/shared - use t1_ms from base metrics
    const metricsV1: FilterPerformanceMetricsV1 = {
      ...metrics,
      api_type: shouldUseNewApi === true ? 'new_api' : 'old_api',
      new_api_t2_ms: new_api_t2_ms || 0,
      old_api_t2_ms: old_api_t2_ms || 0,
      is_internal_user: Boolean(isInternal),
      internal_enabled: Boolean(internal_enabled),
      external_enabled: Boolean(external_enabled),
      user_email: (email as string) || null,
      user_org: (org as string) || null,
      user_name: currentUser?.name || currentUser?.displayName || null,
      access_level: getAccessLevel(currentUser) || null
    }

    // Send to PostHog with V1 reporter
    posthogPerformanceReporterV1(metricsV1)

    // Save fresh data to cache
    if (allFiltersDataForUser && allFiltersDataForUser.length > 0) {
      saveDataToIndexedDB('AllFiltersData', allFiltersDataForUser)
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(`%c⚡ [FilterContext] Performance Report V1`, 'font-weight: bold; color: #2196F3')
      console.log(`  API Type: ${metricsV1.api_type}`)
      console.log(`  T1 (Cache): ${t1_ms ? t1_ms.toFixed(0) + 'ms' : 'N/A'}`)
      console.log(`  Load Time: ${loadTime_ms.toFixed(0)}ms (${dataWasStale ? 'Stale' : usedCache ? 'Fresh Cache' : 'No Cache'})`)
      console.log(`  New API T2: ${new_api_t2_ms ? new_api_t2_ms.toFixed(0) + 'ms' : 'N/A'}`)
      console.log(`  Old API T2: ${old_api_t2_ms ? old_api_t2_ms.toFixed(0) + 'ms' : 'N/A'}`)
      console.log(`  Filters: ${allFiltersDataForUser?.length || 0}`)
      console.log(`  User Type: ${isInternal ? 'Internal' : 'External'}`)
    }
  }

  const updateFiltersWithNewData = (isManualUpdate?: boolean) => {
    console.log('🔄 [FilterContext] updateFiltersWithNewData called - stale data being refreshed')
    // Reset performance tracking for new load cycle
    resetPerformanceTracking()

    // Start T2 timing for new load cycle
    t2StartRef.current = performance.now()

    setFilterDataLoading(true)
    // Load from IndexedDB first (T1 timing)
    getFiltersDataFromIndexedDb()
    // Then create graph instances with the new data (T2 timing ends in createGraphInstances)
    createGraphInstances()
    setIsFilterChanged(false)
    setShowFilterChangedNotification(false)
    openSuccess('Filters have been updated')

    const message = 'Filters update success'
    let title = `Filters updated ${isManualUpdate ? 'manually' : 'automatically'} for\n`
    title =
      title + (org ? `\`org: ${org}\`\n` : ``) + (email ? `\`user: ${email}\`\n` : ``) + `\`page URL: <${window.location.href}|${window.location.pathname}>\`\n`
    sendSlackNotification({
      message,
      title,
      channel: 'fe-logs'
    })
  }

  // ? handle filter update progress timer
  React.useEffect(() => {
    let timer
    if (isFilterChanged) {
      setAutoUpdateFiltersProgress(100)
      timer = setInterval(() => {
        setAutoUpdateFiltersProgress((prevProgress) => (prevProgress < 0 ? 100 : prevProgress - 10))
      }, automaticallyUpdateFiltersDuration / 10)
    }
    return () => {
      clearInterval(timer)
    }
  }, [isFilterChanged])

  const getFilterValues: GetFilterValues = <T extends 'array' | 'object'>(
    filterKeys: string[],
    obj: { dataType?: T; dateWithHyphen?: boolean; emptyForAllSelected?: boolean }
  ): T extends 'array' ? string[] : getFiltersV2ReturnType => {
    // function getFilterValues(filterKeys: string[], obj?: { dataType: 'array'; dateWithHyphen?: boolean; emptyForAllSelected?: boolean }): string[]
    // function getFilterValues(filterKeys: string[], obj?: { dataType?: 'object'; dateWithHyphen?: boolean; emptyForAllSelected?: boolean }): getFiltersV2ReturnType
    // function getFilterValues(filterKeys: string[], obj?: { dataType?: 'array' | 'object'; dateWithHyphen?: boolean; emptyForAllSelected?: boolean }) {
    const { dataType, dateWithHyphen, emptyForAllSelected = true } = obj || {}
    const activeGraph = filterGraphManager.getGraph(activeGraphId)
    const graphFilters = activeGraph.getFilters()
    const result = [] as { key: string; value: string | boolean; forArrayOnly?: boolean; extraForObj?: { key: string; value: string | boolean }[] }[]
    filterKeys.forEach((key) => {
      const drillDownContextValue = getDrillDownContextFilter ? getDrillDownContextFilter(key) : null
      if (drillDownContextValue) {
        result.push({ key: `${key}_in`, value: drillDownContextValue })
        return
      }
      if (key in graphFilters) {
        let value: string
        const allOptionsSelectedAndUserHasAllLocations =
          allFiltersDataForUser.length === (allFiltersDataSwr as any).length && emptyForAllSelected && checkedIfAllOptionsSelected(activeGraphId, key)

        const accessLevel = getAccessLevel(currentUser)
        const isBusinessAdminOrFinanceTeam = accessLevel === AccessLevelEnum.BUSINESS_ADMIN || accessLevel === AccessLevelEnum.FINANCE_TEAM

        if (allOptionsSelectedAndUserHasAllLocations && (!shouldUseNewApi || isBusinessAdminOrFinanceTeam)) {
          // For old API or business admins/finance team: send empty string when all options are selected
          value = ''
        } else {
          // For new API (non-admin users) or when not all options are selected: send all selected values
          const intersectionSet = activeGraph.getVisibleSelectedIntersectionNodes(key)
          const intersectionArr = [...intersectionSet]
          value = intersectionArr.map((e) => e.value).join('|')
        }
        if (key === 'b_name_id') {
          result.push({ key: 'b_name_ids_in', value })
        } else {
          result.push({ key: `${key}_in`, value })
        }
      } else if (key === 'platform') {
        const intersectionSet = activeGraph.getVisibleSelectedIntersectionNodes('vb_platform')
        const intersectionArr = [...intersectionSet]
        const value = intersectionArr.map((e) => e.value).join('|')
        result.push({ key: 'vb_platform_in', value })
      } else if (key === 'chain') {
        result.push({ key: `${key}_in`, value: get(currentUser, 'org', '') })
      } else if (key === 'start_date') {
        result.push({ key: `${key}_in`, value: dateRange?.start.format(dateWithHyphen ? 'YYYY-MM-DD' : 'YYYYMMDD') || '' })
      } else if (key === 'end_date') {
        result.push({ key: `${key}_in`, value: dateRange?.end.format(dateWithHyphen ? 'YYYY-MM-DD' : 'YYYYMMDD') || '' })
      } else if (key === 'granularity') {
        let start_date = dateRange?.start
        let end_date = dateRange?.end
        if (start_date && end_date) {
          result.push({
            key: `${key}_in`,
            value: findGranularityByDates({ start_date, end_date }),
            extraForObj: [{ key: 'should_adjust_dates', value: true }]
          })
        }
      } else if (key.length > 0 && key.startsWith('_')) {
        result.push({ key: '', value: key.slice(1), forArrayOnly: true })
      }
    })

    type Type = T extends 'array' ? string[] : getFiltersV2ReturnType

    if (dataType === 'array') {
      return result.map(({ value }) => value.toString()) as Type
    }

    const objResult = {} as getFiltersV2ReturnType
    result.forEach(({ key, value, forArrayOnly, extraForObj }) => {
      if (forArrayOnly) {
        return
      }
      objResult[key] = value
      if (extraForObj) {
        extraForObj.forEach(({ key, value }) => {
          objResult[key] = value
        })
      }
    })
    return objResult as Type
  }

  // ********************* data exposed by provider ***********************
  /**
   * This function will take the list of keys (like ['b_name', 'vb_name', 'vb_platform']).
   * User can also pass custom value in keys followed by an '_' (like ['b_name', '_some_customValue', 'vb_name'])
   * @param filterKeys
   * @returns filter values and other data in an array
   */
  const getFilters = (filterKeys: string[]) => {
    return getFilterValues(filterKeys, { dataType: 'array' })
  }

  /**
   * This function will take the list of keys (like ['b_name', 'vb_name', 'vb_platform']).
   * @param filterKeys
   * @returns filter values and other data in an object
   */
  const getFiltersV2 = (filterKeys: string[], dateWithHyphen?: boolean, emptyForAllSelected: boolean = true) => {
    return getFilterValues(filterKeys, { dataType: 'object', dateWithHyphen, emptyForAllSelected })
  }

  const getAllPossibleFilters = (filterKeys: string[], graphId?: string) => {
    const graph = graphId ? filterGraphManager.getGraph(graphId) : activeGraph.current
    const result = {}
    filterKeys.forEach((key) => {
      const allPossibleNodes = graph.getAllPossibleNodesByKey(key)
      const value = allPossibleNodes.map((e) => e.value)
      result[key] = value
    })
    return result
  }

  const getUrlWithFilters = () => {
    const defaultFilterKeys = getFilterKeys()
    const selectedFilterValuesJoined = {}
    const result = getFiltersV2(defaultFilterKeys)
    defaultFilterKeys.forEach((key) => {
      selectedFilterValuesJoined[key] = result[`${key}_in`]
    })
    const params = createSearchParams({
      filters: JSON.stringify(selectedFilterValuesJoined),
      start_date: dateRange?.start.format('YYYYMMDD') || '',
      end_date: dateRange?.end.format('YYYYMMDD') || ''
    })

    let url = new URL(window.location.href)
    params.forEach((param, key) => {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key)
      }
      url.searchParams.set(key, param)
    })

    return url.href
  }

  // ************************** actions exposed by provider *****************************
  async function refreshFilters() {
    console.log('🔄 [FilterContext] refreshFilters called - org/access level changed')
    localforage.clear()
    filterGraphManager.clearGraphs()
    // Reset performance tracking for new load cycle
    // Pass true to indicate data is stale due to org/access level change
    resetPerformanceTracking(true)

    // Start T2 timing for new load cycle
    t2StartRef.current = performance.now()

    setTriggerSwr((prev) => prev + 1)
  }

  const selectFiltersFromContext = (filterKey: string, values: (string | number)[], graphId?: string) => {
    const graph = graphId ? filterGraphManager.getGraph(graphId) : activeGraph.current
    const selectedFilterNodes = graph.getAllPossibleNodesByKey(filterKey).filter((node) => values.includes(node.value))
    // ? if key is single select and no matching values are found, then select first value
    if (selectedFilterNodes.length === 0 && graph.getSingleSelectByKey(filterKey)) {
      selectedFilterNodes.push(graph.getAllPossibleNodesByKey(filterKey)[0])
    }
    graph.setSelected(filterKey, selectedFilterNodes)

    const defaultFilterKeys = getFilterKeys()
    defaultFilterKeys.forEach((observerFilterKey) => {
      if (observerFilterKey !== filterKey) updateObserverVisibleNodesFromGraph(observerFilterKey, selectedFilterNodes, graph)
    })
    reRenderFilterContext()
  }

  // ************** dates related code *****************
  React.useEffect(() => {
    setDrillDownAllFiltersData(allFiltersDataForUser)
  }, [allFiltersDataForUser])

  React.useEffect(() => {
    setDrillDownBusinessRefreshBnameData(businessRefreshBnameLevelData)
  }, [businessRefreshBnameLevelData])

  const dateRange = activeGraph.current.getDateRange()
  const lastDate = activeGraph.current.getLastDate()

  let findGranularityByDates = React.useCallback(
    function ({ start_date, end_date }: { start_date: Moment; end_date: Moment }) {
      let diff = moment(end_date).diff(moment(start_date), 'days')
      if (diff > 120) {
        return 'week'
      } else {
        return 'day'
      }
    },
    [...getFilters(['start_date', 'end_date'])]
  )

  // ************************** client side storage ********************************
  const saveSelectFiltersToSessionStorage = (serializedSelectedFilters: string) => {
    sessionStorage.setItem('selectedFilters', serializedSelectedFilters)
    sessionStorage.setItem('start_date', dateRange?.start.format('YYYYMMDD') || '')
    sessionStorage.setItem('end_date', dateRange?.end.format('YYYYMMDD') || '')
  }

  const getFiltersDataFromIndexedDb = async () => {
    try {
      // Determine which API will be used (for logging purposes)
      const apiType = shouldUseNewApi === true ? 'new_api' : shouldUseNewApi === false ? 'old_api' : null

      // Start general T1 timing when loading from cache
      // Note: T1 (cache) is general/shared across both APIs
      if (!t1StartRef.current) {
        t1StartRef.current = performance.now()
        setHydrationState(HydrationState.LOADING_CACHE)
      }

      const cachedData = await getDataFromIndexedDB('AllFiltersData')
      if (cachedData && cachedData.length > 0) {
        // Cache loaded successfully - end general T1 timing
        t1EndRef.current = performance.now()
        console.log('⏱️ [FilterContext] T1 ended (cache load):', t1EndRef.current - (t1StartRef.current || 0), 'ms')

        cacheDataRef.current = cachedData
        setHydrationState(HydrationState.CACHE_LOADED)

        // Load into graph manager
        setGraphToManager('main', cachedData)
        setFilterDataLoading(false)

        // Simple cache stats - just check if we have data
        setCacheStats({ hasData: true })

        if (process.env.NODE_ENV === 'development') {
          console.debug('[FilterContext Performance] Cache loaded:', {
            t1_ms: t1EndRef.current - t1StartRef.current,
            filterCount: cachedData.length,
            apiType
          })
        }
      }
    } catch (err) {
      handleError("Can't fetch Filters Data from IDB: " + err.message)
    }
  }

  // *********************** drill down related code *************************
  React.useEffect(() => {
    if (drillDownStack.length > 0) {
      return
    }
    const { start_date_in: start, end_date_in: end } = getFiltersV2(['start_date', 'end_date'], true)
    if (start && end) {
      setDrillDownDateRange({ start: moment(start), end: moment(end) })
    }
  }, [drillDownStack, ...getFilters(['start_date', 'end_date'])])

  // *********************** module date filter related code ***********************
  const moduleLastDates: { [key: string]: Moment } = useMemo(() => {
    const moduleLastDates = {}

    Object.entries(moduleDateFilterData?.module_bname_data || {}).forEach(([key, moduleData]) => {
      moduleLastDates[key] = getNewLastDate(
        moduleData.bnames || [],
        activeGraph.current,
        moduleData.calculated_percentile ?? 90
      )
    })

    return moduleLastDates
  }, [moduleDateFilterData, Array.from(activeGraph.current.getVisibleSelectedIntersectionNodes('b_name') || [])?.join('|')])

  const activeModuleEndDate = useMemo(() => {
    const moduleData = moduleDateFilterData?.module_bname_data
    if (moduleData) {
      const moduleName = getModuleFromPathname(location.pathname, categorizedRoutes)

      return moduleLastDates[moduleName] ?? null
    }
    return null
  }, [moduleDateFilterData, location.pathname, categorizedRoutes, moduleLastDates])

  React.useEffect(() => {
    if (!currentUser?.org) return
    if (filterApiConfigLoading) return

    const getModuleDateFilterData = async () => {
      try {
        setModuleDateFilterLoading(true)

        // Call date_filter_config API from app-client
        // API returns: { module_bname_data: { [module]: { calculated_percentile, bnames: [{b_name, end_date}] } }, overrides }
        const res = await getDateFilterConfig()

        setModuleDateFilterData(res)
      } catch (err) {
        console.error('Error in module date filter data', err)
        handleError('Error in module date filter data')
      } finally {
        setModuleDateFilterLoading(false)
      }
    }

    getModuleDateFilterData()
  }, [currentUser?.org, filterApiConfigLoading])

  const contextValue = React.useMemo(() => {
    return {
      businessRefreshBnameLevelData,
      userAssignedBnamesJoined,
      userAssignedBnames: userAssignedBnamesSwr,
      updateActiveGraph,
      activeGraph: filterGraphManager.getGraph(activeGraphId),
      activeGraphId,
      setActiveGraphId,
      mainGraph,
      filterConfig,
      reRenderVar,
      reRenderFilterContext,
      dateRange,
      lastDate,
      getFilterValues,
      getFilters,
      getFiltersV2,
      getAllPossibleFilters,
      getUrlWithFilters,
      selectFiltersFromContext,
      refreshFilters,
      filterDataLoading,
      moduleDateFilterLoading,
      moduleLastDates,
      activeModuleEndDate,
      isFilterChanged,
      showFilterChangedNotification,
      setShowFilterChangedNotification,
      updateFiltersWithNewData,
      autoUpdateFiltersProgress,
      moduleDateFilterData,
      userAssignedBnamesSwr,
      allFiltersDataForUser,
      // Performance tracking
      performanceMetrics: {
        t1_ms: t1EndRef.current && t1StartRef.current ? t1EndRef.current - t1StartRef.current : null,
        t2_ms: t2EndRef.current && t2StartRef.current ? t2EndRef.current - t2StartRef.current : null,
        loadTime_ms: (() => {
          const t1 = t1EndRef.current && t1StartRef.current ? t1EndRef.current - t1StartRef.current : null
          const t2 = t2EndRef.current && t2StartRef.current ? t2EndRef.current - t2StartRef.current : null
          const dataWasStale = isFilterChanged
          const usedCache = !!cacheDataRef.current
          return dataWasStale || !usedCache ? t2 || 0 : t1 || t2 || 0
        })(),
        cacheHit: !!cacheDataRef.current && !isFilterChanged,
        dataWasStale: isFilterChanged,
        hydrationState
      },
      cacheStats
    }
  }, [
    businessRefreshBnameLevelData,
    userAssignedBnamesJoined,
    userAssignedBnamesSwr,
    updateActiveGraph,
    activeGraphId,
    mainGraph,
    filterConfig,
    reRenderVar,
    dateRange,
    lastDate,
    getFilterValues,
    getFilters,
    getFiltersV2,
    getAllPossibleFilters,
    getUrlWithFilters,
    selectFiltersFromContext,
    refreshFilters,
    filterDataLoading,
    moduleDateFilterLoading,
    moduleLastDates,
    activeModuleEndDate,
    isFilterChanged,
    showFilterChangedNotification,
    setShowFilterChangedNotification,
    updateFiltersWithNewData,
    autoUpdateFiltersProgress,
    moduleDateFilterData,
    userAssignedBnamesSwr,
    allFiltersDataForUser,
    hydrationState,
    cacheStats,
    isFilterChanged
  ])

  return <NewFilterContext.Provider value={contextValue}>{props.children}</NewFilterContext.Provider>
}
